
import { doctorService } from './doctorService';
import { patientService } from './patientService';
import { appointmentService } from './appointmentService';
import { Doctor, Patient, Appointment } from '../types';
import { toast } from '@/components/ui/sonner';

/**
 * Helper utility to create a sample record for doctors, patients, and appointments
 * This is meant to be used for demonstration purposes
 */
export const sampleRecordCreation = async (): Promise<{
  doctor: Doctor | null;
  patient: Patient | null;
  appointment: Appointment | null;
}> => {
  let createdDoctor: Doctor | null = null;
  let createdPatient: Patient | null = null;
  let createdAppointment: Appointment | null = null;

  try {
    // Create a doctor
    const doctorData = {
      name: "Dr. Emily Johnson",
      specialty: "Cardiology",
      bio: "Dr. Johnson is a board-certified cardiologist with over 10 years of experience in treating heart conditions. She specializes in preventive cardiology and heart failure management.",
      image: "https://picsum.photos/id/64/200/200", // Using a placeholder image
      weeklyOffDays: [0, 6], // Sunday and Saturday off
      slotDuration: 30,
      startTime: "09:00",
      endTime: "17:00"
    };

    createdDoctor = await doctorService.create(doctorData);
    toast.success("Doctor record created successfully");

    // Create a patient
    const patientData = {
      fullName: "Michael Smith",
      email: "michael.smith@example.com",
      phone: "555-123-4567",
      address: "123 Main Street",
      city: "New York",
      state: "NY",
      country: "USA",
      zipCode: "10001",
      gender: "Male",
      age: 42,
      notes: "Patient has a history of hypertension."
    };

    createdPatient = await patientService.create(patientData);
    toast.success("Patient record created successfully");

    // If both doctor and patient were created successfully, create an appointment
    if (createdDoctor && createdPatient) {
      // Set the appointment date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const appointmentDate = tomorrow.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      const appointmentData = {
        doctorId: createdDoctor.id,
        date: appointmentDate,
        timeSlot: "10:00 AM",
        patientName: createdPatient.fullName,
        patientEmail: createdPatient.email,
        patientPhone: createdPatient.phone,
        patientAddress: createdPatient.address,
        patientGender: createdPatient.gender,
        patientAge: createdPatient.age,
        status: "confirmed" as const
      };

      createdAppointment = await appointmentService.create(appointmentData);
      toast.success("Appointment created successfully");
    }

    return {
      doctor: createdDoctor,
      patient: createdPatient,
      appointment: createdAppointment
    };
  } catch (error) {
    console.error("Error creating sample records:", error);
    toast.error("Failed to create sample records. Please try again.");
    return {
      doctor: createdDoctor,
      patient: createdPatient,
      appointment: createdAppointment
    };
  }
};
